import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid2,
  Box,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonAdd as PersonAddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  CheckCircle as CheckCircleIcon,
  Nature as NatureIcon,
  Face as FaceIcon,
  Check as CheckIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { signup, getUsers, checkUsername } from "../api/api";
import "./style.css";

const FIGURINE_AVATARS = [
  "https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/1.02464a56.png",
  "https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/2.b977faab.png",
  "https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/3.4df853b4.png",
  "https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/4.4457fbce.png"
];
const MALE_SEEDS = ["Mason", "Eden", "Adrian", "Kingston", "Christopher", "Leo", "Alexander", "Wyatt", "George", "Arthur"];
const FEMALE_SEEDS = ["Jessica", "Katherine", "Valentina", "Sophia", "Isabella", "Mia", "Emma", "Olivia", "Ava", "Charlotte"];

const getDiceBearUrl = (style, seed) => {
  const baseUrl = process.env.REACT_APP_DICEBEAR_BASE_URL || "https://api.dicebear.com/9.x";
  return `${baseUrl}/${style}/svg?seed=${seed}`;
};

function Signup() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  
  // Username check states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // 'available', 'taken', or null
  const [suggestions, setSuggestions] = useState([]);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (username.length < 3) {
        setUsernameStatus(null);
        setSuggestions([]);
        return;
    }

    const timer = setTimeout(async () => {
        setIsCheckingUsername(true);
        try {
            const { available } = await checkUsername(username);
            setUsernameStatus(available ? 'available' : 'taken');
            
            if (!available) {
                // Generate suggestions
                const base = username.replace(/\d+$/, "");
                const newSuggestions = [
                    `${base}${Math.floor(Math.random() * 999)}`,
                    `${base}_${Math.floor(Math.random() * 99)}`,
                    `${base}Nexus`,
                    `${base}Chat`
                ].slice(0, 3);
                setSuggestions(newSuggestions);
            } else {
                setSuggestions([]);
            }
        } catch (err) {
            console.error("Username check error", err);
        } finally {
            setIsCheckingUsername(false);
        }
    }, 600); // 600ms delay

    return () => clearTimeout(timer);
  }, [username]);

  const natureAvatars = useMemo(() => 
    FIGURINE_AVATARS.map((url, i) => ({ seed: `Figurine ${i + 1}`, url: url, type: 'nature' })), 
  []);

  const genderAvatars = useMemo(() => {
    if (gender === "Male") {
        return MALE_SEEDS.map(s => ({ 
            seed: s, 
            url: getDiceBearUrl("micah", s) + "&hair=fonze,mrClean,dougFunny,mrT&facialHairProbability=50", 
            type: 'gender' 
        }));
    }
    if (gender === "Female") {
        return FEMALE_SEEDS.map(s => ({ 
            seed: s, 
            url: getDiceBearUrl("micah", s) + "&hair=full,pixie&facialHairProbability=0", 
            type: 'gender' 
        }));
    }
    return [];
  }, [gender]);

  const isFormComplete =
    username && 
    password && 
    firstName && 
    lastName && 
    gender && 
    age && 
    (avatar || selectedFile);

  const validateUsername = (username) => {
    // Instagram-style: 3-30 characters, letters, numbers, periods, underscores
    const regex = /^[a-zA-Z0-9._]{3,30}$/;
    return regex.test(username);
  };

  const handleGenderChange = (event, newGender) => {
    if (newGender !== null) {
      setGender(newGender);
      
      // If the currently selected avatar is gender-specific and the gender changed, clear it
      if (avatar && !avatar.startsWith("data:")) {
          const isNature = natureAvatars.some(a => a.url === avatar);
          if (!isNature) {
              setAvatar(null);
          }
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size should be less than 5MB");
        return;
      }
      setSelectedFile(file);
      setAvatar(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAgeChange = (e) => {
    const value = e.target.value;
    // Strictly allow only digits and prevent non-numeric entry
    if (/^\d*$/.test(value)) {
        setAge(value);
    }
  };

  const handleSignup = async () => {
    setError("");
    if (!validateUsername(username)) {
      setError("Username must be 3-30 characters and can only contain letters, numbers, periods, and underscores");
      return;
    }

    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 18 || ageNum > 70) {
        setError("Age must be between 18 and 70");
        return;
    }

    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("gender", gender);
    formData.append("age", age);
    
    if (selectedFile) {
      formData.append("image", selectedFile);
    } else if (avatar) {
      formData.append("avatar", avatar);
    }

    try {
      const res = await signup(formData);
      localStorage.setItem("isAuthenticated", "true");
      if (res.user) {
        localStorage.setItem("user", JSON.stringify(res.user));
      }
      navigate("/chat");
    } catch (error) {
      setError(error.response?.data?.message || "Signup failed, please try again");
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const avatarBoxStyle = (url) => ({
    width: { xs: 60, sm: 70 },
    height: { xs: 60, sm: 70 },
    borderRadius: "12px",
    cursor: "pointer",
    bgcolor: "rgba(0,0,0,0.03)",
    border: avatar === url ? "3px solid #111827" : "2px solid rgba(0,0,0,0.1)",
    position: "relative",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    overflow: "hidden",
    "&:hover": {
      transform: "scale(1.1) translateY(-5px)",
      borderColor: "rgba(0,0,0,0.3)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)"
    }
  });

  const textFieldStyle = {
    color: '#111827',
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: 'rgba(0,0,0,0.1)',
      borderRadius: '16px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#111827',
      boxShadow: '0 0 15px rgba(0,0,0,0.05)'
    },
    '& .MuiInputBase-input': {
      padding: '16px 20px'
    }
  };

  return (
    <Box sx={{ 
      minHeight: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
      background: "transparent", py: 4, fontFamily: "'Inter', sans-serif" 
    }}>
      <Container maxWidth="md" sx={{ zIndex: 50, pb: 10 }}>
        <Paper elevation={0} className="fade-in" sx={{
          p: { xs: 3, md: 5 }, borderRadius: '24px', background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(24px)", boxShadow: "0 16px 40px -10px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.04)", color: "#111827"
        }}>
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography variant="h3" sx={{ 
              fontFamily: "'Anton', sans-serif", fontWeight: 400, mb: 1, letterSpacing: "-0.02em", color: "#111827", textTransform: 'uppercase'
            }}>
              SIGN UP
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#4B5563', fontSize: '1rem' }}>
              Create an account
            </Typography>
          </Box>

          {error && <Fade in><Alert severity="error" sx={{ mb: 3, borderRadius: 3, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#B91C1C', border: '1px solid rgba(239, 68, 68, 0.2)' }}>{error}</Alert></Fade>}

          <Grid2 container spacing={2.5}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                InputProps={{ sx: textFieldStyle }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)}
                InputProps={{ sx: textFieldStyle }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField 
                fullWidth 
                label="Username" 
                placeholder="e.g., alex_dev" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{ 
                    sx: textFieldStyle,
                    endAdornment: (
                        <InputAdornment position="end">
                            {isCheckingUsername && <CircularProgress size={20} sx={{ color: '#111827' }} />}
                            {!isCheckingUsername && usernameStatus === 'available' && <CheckIcon sx={{ color: '#10B981' }} />}
                            {!isCheckingUsername && usernameStatus === 'taken' && <ErrorIcon sx={{ color: '#EF4444' }} />}
                        </InputAdornment>
                    )
                }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
                helperText={
                    usernameStatus === 'taken' ? (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ color: '#EF4444', display: 'block', mb: 1 }}>
                                Username taken. Try these:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {suggestions.map(s => (
                                    <Chip 
                                        key={s} 
                                        label={s} 
                                        size="small" 
                                        onClick={() => setUsername(s)}
                                        sx={{ 
                                            bgcolor: 'rgba(17, 24, 39, 0.05)', 
                                            color: '#111827', 
                                            border: '1px solid rgba(17, 24, 39, 0.1)',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: 'rgba(17, 24, 39, 0.1)' }
                                        }} 
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : username.length > 0 && !validateUsername(username) ? (
                        "3-30 chars, letters, numbers, periods, underscores"
                    ) : null
                }
                FormHelperTextProps={{ sx: { color: '#6B7280' } }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField fullWidth label="Password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                InputProps={{ 
                  endAdornment: <InputAdornment position="end"><IconButton onClick={handleClickShowPassword} edge="end" sx={{ color: '#6B7280' }}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                  sx: textFieldStyle 
                }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
              />
            </Grid2>
            
            <Grid2 size={{ xs: 12, sm: 5 }}>
              <TextField 
                fullWidth 
                label="Age" 
                value={age} 
                onChange={handleAgeChange}
                inputProps={{ 
                    min: 18, 
                    max: 70,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                }}
                InputProps={{ sx: textFieldStyle }}
                InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 7 }}>
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={gender}
                  exclusive
                  onChange={handleGenderChange}
                  fullWidth
                  sx={{ 
                    bgcolor: 'transparent', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.1)',
                    '& .MuiToggleButton-root': {
                      border: 'none', color: '#6B7280', textTransform: 'none', py: 1.5, borderRadius: '14px', m: '4px',
                      '&.Mui-selected': { 
                        bgcolor: '#111827', color: 'white', fontWeight: 600,
                        '&:hover': { bgcolor: '#000' }
                      },
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', color: '#111827' }
                    }
                  }}
                >
                  <ToggleButton value="Male"><MaleIcon sx={{ mr: 1, fontSize: 20 }} /> Male</ToggleButton>
                  <ToggleButton value="Female"><FemaleIcon sx={{ mr: 1, fontSize: 20 }} /> Female</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Box sx={{ mt: 2, p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.4)', border: '1px solid rgba(0,0,0,0.05)' }}>
                <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 700, color: "#4B5563", display: 'flex', alignItems: 'center', gap: 1 }}>
                   Choose Profile Avatar
                </Typography>
                
                <Box display="flex" alignItems="center" gap={3} mb={4}>
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}
                    sx={{ 
                      borderRadius: 3, textTransform: "none", py: 1.5, px: 3, fontWeight: 600,
                      borderColor: selectedFile ? "#10B981" : "rgba(0,0,0,0.2)",
                      color: selectedFile ? "#10B981" : "#111827",
                      "&:hover": { borderColor: "#111827", bgcolor: "rgba(0,0,0,0.02)" }
                    }}
                  >
                    {selectedFile ? "File Selected" : "Upload Custom Photo"}
                    <input type="file" hidden accept="image/*" ref={fileInputRef} onChange={handleFileChange} />
                  </Button>
                  
                  {previewUrl && (
                    <Box sx={{ position: "relative" }}>
                      <Avatar src={previewUrl} sx={{ width: 65, height: 65, border: "3px solid #10B981", boxShadow: "0 0 20px rgba(16, 185, 129, 0.3)" }} />
                      <IconButton size="small" onClick={clearSelectedFile} sx={{ position: "absolute", top: -8, right: -8, bgcolor: "#EF4444", color: "white", "&:hover": { bgcolor: "#DC2626" } }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Box>
                  )}
                </Box>

                {/* Premium 3D Themes Group */}
                <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, fontWeight: 700, textTransform: 'uppercase' }}>
                  <NatureIcon sx={{ fontSize: 16 }} /> Premium 3D Themes
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                  {natureAvatars.map((item, index) => {
                    const bgs = ['#F4845F', '#6BBF7A', '#E882B4', '#6EB5FF'];
                    const bgColor = bgs[index % bgs.length];
                    return (
                      <Tooltip title={item.seed} key={item.seed}>
                        <Box onClick={() => { setAvatar(item.url); setSelectedFile(null); setPreviewUrl(null); }} sx={{ ...avatarBoxStyle(item.url), bgcolor: bgColor }}>
                          <Box component="img" src={item.url} alt={item.seed} sx={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom center", p: 0.5 }} />
                          {avatar === item.url && <CheckCircleIcon sx={{ position: 'absolute', top: 2, right: 2, color: '#111827', fontSize: 18, bgcolor: 'white', borderRadius: '50%' }} />}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Gender Specific Group */}
                {gender && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, fontWeight: 700, textTransform: 'uppercase' }}>
                      <FaceIcon sx={{ fontSize: 16 }} /> {gender} Avatars
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      {genderAvatars.map((item) => (
                        <Tooltip title={item.seed} key={item.seed}>
                          <Box onClick={() => { setAvatar(item.url); setSelectedFile(null); setPreviewUrl(null); }} sx={avatarBoxStyle(item.url)}>
                            <Box component="img" src={item.url} alt={item.seed} sx={{ width: "100%", height: "100%" }} />
                            {avatar === item.url && <CheckCircleIcon sx={{ position: 'absolute', top: 2, right: 2, color: '#111827', fontSize: 18, bgcolor: 'white', borderRadius: '50%' }} />}
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12 }} sx={{ mt: 3 }}>
              <Button fullWidth variant="contained" onClick={handleSignup} disabled={!isFormComplete}
                startIcon={<PersonAddIcon />}
                sx={{
                  py: 2, fontSize: "1.1rem", fontWeight: 700, borderRadius: 4, textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif",
                  background: isFormComplete ? "#111827" : "rgba(0,0,0,0.05)",
                  color: isFormComplete ? "white" : "rgba(0,0,0,0.3)",
                  boxShadow: isFormComplete ? "0 10px 20px rgba(0, 0, 0, 0.1)" : "none",
                  "&:hover": { background: "#000", transform: "translateY(-2px)" },
                  "&:disabled": { color: "rgba(0,0,0,0.3)", background: "rgba(0,0,0,0.05)" }
                }}
              >
                Sign Up
              </Button>
            </Grid2>

            <Grid2 size={{ xs: 12 }} textAlign="center" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                Already have an account? <Link to="/login" style={{ color: "#111827", fontWeight: 700, textDecoration: "none" }}>Log In</Link>
              </Typography>
            </Grid2>
          </Grid2>
        </Paper>
      </Container>
    </Box>
  );
}

export default Signup;